<template>
  <div class="panorama-container" :style="containerStyle">
    <!-- 加载中状态 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">全景图加载中...</div>
      <div class="loading-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: loadProgress + '%' }"></div>
        </div>
        <div class="progress-text">{{ loadProgress }}%</div>
      </div>
    </div>

    <!-- 加载失败状态 -->
    <div v-if="loadError" class="error-overlay">
      <div class="error-icon">⚠️</div>
      <div class="error-text">{{ errorMessage }}</div>
      <button v-if="src" class="retry-btn" @click="retryLoad">重新加载</button>
    </div>

    <!-- 全景图内容（加载时也显示，避免闪烁） -->
    <div class="panorama-content" :class="{ 'is-loading': loading }">
      <!-- 右上角模式切换按钮 -->
      <div v-if="fastUrl" class="mode-toggle-btn" @click="toggleMode">
        {{ useFastMode ? '急速模式' : '高清模式' }}
      </div>

      <div class="panorama-viewer" ref="viewerContainer">
        <canvas ref="canvas"></canvas>
        
        <!-- 批注标记点 -->
        <template v-if="enableAnnotation && showAnnotations">
          <div 
            v-for="annotation in annotations" 
            :key="annotation.id"
            class="annotation-marker"
            :style="getAnnotationPosition(annotation)"
            @mouseenter="handleAnnotationHover"
          >
            <div 
              v-if="canDragAnnotation(annotation)"
              class="marker-dot marker-dot-draggable" 
              :title="annotationMode ? '拖拽移动位置' : '点击查看批注'"
              @mousedown.stop="startDragAnnotation($event, annotation)"
              @touchstart.stop="startDragAnnotation($event, annotation)"
              @click.stop="showAnnotationDetail(annotation)"
            >
              📌
            </div>
            <div 
              v-else
              class="marker-dot" 
              title="点击查看批注"
              @click.stop="showAnnotationDetail(annotation)"
            >
              📌
            </div>
            <div 
              class="annotation-content-inline"
              @click.stop="showAnnotationDetail(annotation)"
            >
              <div class="annotation-text-inline">{{ annotation.content }}</div>
              <div v-if="annotation.replies && annotation.replies.length > 0" class="replies-count-inline">
                💬 {{ annotation.replies.length }} 条回复
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 批注详情弹窗 -->
      <div v-if="selectedAnnotation" class="annotation-modal" @click="closeAnnotationDetail">
        <div class="annotation-content" @click.stop>
          <div class="annotation-header-modal">
            <h3>批注详情</h3>
            <button class="close-btn" @click="closeAnnotationDetail">×</button>
          </div>
          <div class="annotation-body">
            <!-- 主评论 -->
            <div class="comment-item main-comment">
              <div class="comment-header">
                <div class="comment-user-info">
                  <img 
                    v-if="selectedAnnotation.created_by_avatar" 
                    :src="selectedAnnotation.created_by_avatar" 
                    class="user-avatar"
                    @error="handleAvatarError"
                  />
                  <div v-else class="user-avatar-placeholder">{{ getInitials(selectedAnnotation.created_by_name || selectedAnnotation.user_name || '匿名') }}</div>
                  <span class="comment-user">{{ selectedAnnotation.created_by_name || selectedAnnotation.user_name || '匿名用户' }}</span>
                </div>
                <span class="comment-time">{{ formatTime(selectedAnnotation.timestamp) }}</span>
              </div>
              <p class="comment-text">{{ selectedAnnotation.content }}</p>
              <div class="comment-actions">
                <button 
                  v-if="isUserLoggedIn" 
                  class="btn-reply" 
                  @click="startReply(selectedAnnotation.id)"
                >
                  💬 回复
                </button>
                <button 
                  v-if="canDeleteComment(selectedAnnotation)" 
                  class="btn-delete-comment" 
                  @click="deleteAnnotation(selectedAnnotation.id)"
                >
                  🗑️ 删除
                </button>
              </div>
            </div>

            <!-- 回复列表 -->
            <div v-if="selectedAnnotation.replies && selectedAnnotation.replies.length > 0" class="replies-section">
              <div class="replies-header">
                <span class="replies-count">{{ selectedAnnotation.replies.length }} 条回复</span>
                <button 
                  v-if="selectedAnnotation.replies.length > 3 && !showAllReplies" 
                  class="btn-show-more"
                  @click="showAllReplies = true"
                >
                  展开全部
                </button>
                <button 
                  v-if="showAllReplies && selectedAnnotation.replies.length > 3" 
                  class="btn-show-more"
                  @click="showAllReplies = false"
                >
                  收起
                </button>
              </div>
              <div 
                v-for="reply in displayedReplies" 
                :key="reply.id"
                class="comment-item reply-comment"
              >
                <div class="comment-header">
                  <div class="comment-user-info">
                    <img 
                      v-if="reply.created_by_avatar" 
                      :src="reply.created_by_avatar" 
                      class="user-avatar user-avatar-small"
                      @error="handleAvatarError"
                    />
                    <div v-else class="user-avatar-placeholder user-avatar-small">{{ getInitials(reply.created_by_name || reply.user_name || '匿名') }}</div>
                    <span class="comment-user">{{ reply.created_by_name || reply.user_name || '匿名用户' }}</span>
                  </div>
                  <span class="comment-time">{{ formatTime(reply.timestamp) }}</span>
                </div>
                <p class="comment-text">{{ reply.content }}</p>
                <div class="comment-actions">
                  <button 
                    v-if="canDeleteComment(reply)" 
                    class="btn-delete-comment" 
                    @click="deleteReply(reply.id)"
                  >
                    🗑️ 删除
                  </button>
                </div>
              </div>
            </div>

            <!-- 回复输入框 -->
            <div v-if="replyingTo === selectedAnnotation.id" class="reply-input-section">
              <textarea 
                v-model="replyText" 
                placeholder="输入回复内容..."
                class="reply-input"
                rows="3"
                ref="replyInput"
              ></textarea>
              <div class="reply-input-actions">
                <button class="btn-cancel" @click="cancelReply">取消</button>
                <button class="btn-save" @click="saveReply">发送</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'UAVPanorama',
  props: {
    // 全景图片地址
    src: {
      type: String,
      required: true,
    },
    // 快速加载URL（压缩图）
    fastUrl: {
      type: String,
      default: '',
    },
    // 容器宽度
    width: {
      type: [String, Number],
      default: null,
    },
    // 容器高度
    height: {
      type: [String, Number],
      default: null,
    },
    // 项目ID
    projectId: {
      type: [String, Number],
      default: null,
    },
    // 视图ID
    viewId: {
      type: [String, Number],
      default: null,
    },
    // 场景ID
    sceneId: {
      type: [String, Number],
      default: null,
    },
    // 配置选项
    option: {
      type: Object,
      default: () => ({}),
    },
    // 是否启用批注模式（从外部控制）
    enableAnnotationMode: {
      type: Boolean,
      default: false,
    },
    // 是否显示批注标记
    showAnnotations: {
      type: Boolean,
      default: true,
    },
    // // 是否使用快速模式（从外部控制）
    // useFastMode: {
    //   type: Boolean,
    //   default: true,
    // },
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      sphere: null,
      raycaster: null,
      isUserInteracting: false,
      onMouseDownMouseX: null,
      onMouseDownMouseY: null,
      // 内部视角状态（从 option 初始化，但之后独立管理）
      lon: 0,
      onMouseDownLon: 0,
      lat: 0,
      onMouseDownLat: 0,
      phi: 0,
      theta: 0,
      // 内部自动旋转状态（从 option 初始化，但之后独立管理）
      autoRotateEnabled: false,
      // 内部模式状态
      useFastMode: true, // 默认使用快速模式（如果有fast_url）
      // 惯性相关
      velocityX: 0, // 水平速度
      velocityY: 0, // 垂直速度
      lastMouseX: 0, // 上一帧鼠标X
      lastMouseY: 0, // 上一帧鼠标Y
      lastMoveTime: 0, // 上一帧时间
      inertiaActive: false, // 是否正在惯性滚动
      targetLat: 0, // 目标纬度（用于平滑归零）
      // 双指缩放相关
      initialPinchDistance: 0, // 初始双指距离
      pinchInitialFov: 75, // 双指缩放时的初始视场角（用于缩放计算）
      shouldResetLat: false, // 是否应该进行视角归零（用户无操作5秒后）
      lastUserInteraction: 0, // 最后用户操作时间
      annotations: [],
      selectedAnnotation: null,
      animationId: null,
      loading: true,
      loadError: false,
      errorMessage: '',
      loadProgress: 0,
      abortController: null,
      lastEmittedRotation: { x: 0, y: 0, z: 0, zoom: 75 },
      replyingTo: null,
      replyText: '',
      showAllReplies: false,
      annotationUpdateKey: 0,
      canvasElement: null, // 保存canvas引用以便清理事件监听器
      handleOrientationChange: null, // 保存orientationchange处理函数引用
      // 拖拽相关
      draggingAnnotation: null,
      isDraggingAnnotation: false,
    };
  },
  computed: {
    // 容器样式
    containerStyle() {
      const style = {};
      
      if (this.width) {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
      } else {
        style.width = '100%';
      }
      
      if (this.height) {
        style.height = typeof this.height === 'number' ? `${this.height}px` : this.height;
      } else {
        style.height = '100%';
      }
      
      return style;
    },
    // 是否用户已登录
    isUserLoggedIn() {
      try {
        const session = this.$store.getters['session/session'];
        return !!(session && session.id);
      } catch (error) {
        console.warn('无法获取用户登录状态:', error);
        return false;
      }
    },
    // 当前用户ID
    currentUserId() {
      try {
        const session = this.$store.getters['session/session'];
        return session ? session.id : null;
      } catch (error) {
        return null;
      }
    },
    // 是否启用批注功能（显示批注标记）
    enableAnnotation() {
      return this.option.enableAnnotation !== false;
    },
    // 批注模式（从外部prop控制）
    annotationMode() {
      return this.enableAnnotationMode;
    },
    // 初始经度
    initialLon() {
      return this.option.initialLon || 0;
    },
    // 初始纬度
    initialLat() {
      return this.option.initialLat || 0;
    },
    // 初始视场角
    initialFov() {
      return this.option.initialFov || 75;
    },
    // 自动旋转速度
    autoRotateSpeed() {
      return this.option.autoRotateSpeed || 0.025;
    },
    // 球体半径
    sphereRadius() {
      return this.option.sphereRadius || 500;
    },
    // 球体分段数
    sphereSegments() {
      return this.option.sphereSegments || { width: 128, height: 64 };
    },
    // 显示的回复列表（最近3条或全部）
    displayedReplies() {
      if (!this.selectedAnnotation || !this.selectedAnnotation.replies) {
        return [];
      }
      if (this.showAllReplies || this.selectedAnnotation.replies.length <= 3) {
        return this.selectedAnnotation.replies;
      }
      // 只显示最近3条
      return this.selectedAnnotation.replies.slice(-3);
    },
  },
  watch: {
    src(newVal, oldVal) {
      if (newVal && newVal !== oldVal && newVal) {
        this.loadPanorama();
      }
    },
    viewId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadAnnotations();
      }
    },
    // 监听内部状态 useFastMode（不是 prop，是 data 中的值）
    useFastMode(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadPanorama();
      }
    },
    // 监听 option prop 的属性变化，同步到内部状态
    // 注意：option 是 prop，我们只读取它的值，不修改它
    'option.x': function(val) {
      if (val !== undefined && !this.isUserInteracting) {
        this.lon = val;
      }
    },
    'option.y': function(val) {
      if (val !== undefined && !this.isUserInteracting) {
        this.lat = val;
      }
    },
    'option.zoom': function(val) {
      if (val !== undefined && this.camera) {
        this.camera.fov = val;
        this.camera.updateProjectionMatrix();
      }
    },
  },
  mounted() {
    // 设置初始角度（优先使用option中的x, y值）
    this.lon = this.option.x !== undefined ? this.option.x : this.initialLon;
    this.lat = this.option.y !== undefined ? this.option.y : this.initialLat;
    this.autoRotateEnabled = this.option.autoRotate !== false;
    this.lastUserInteraction = Date.now(); // 初始化最后操作时间
    
    // 使用 $nextTick 确保 DOM 完全渲染后再初始化 Three.js
    this.$nextTick(() => {
      this.initThreeJS();
      if (this.enableAnnotation && this.viewId) {
        this.$api.afterLogin(() => {
          this.loadAnnotations();
        });
      }
    });
    
    window.addEventListener('resize', this.onWindowResize);
    // 监听横竖屏切换
    this.handleOrientationChange = () => {
      // 横竖屏切换时，延迟更新相机视角，确保容器尺寸已更新
      setTimeout(() => {
        this.onWindowResize();
      }, 100);
    };
    window.addEventListener('orientationchange', this.handleOrientationChange);
  },
  beforeDestroy() {
    // 取消正在进行的加载
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // 停止动画循环
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // 清理canvas事件监听器
    if (this.canvasElement) {
      this.canvasElement.removeEventListener('mousedown', this.onMouseDown);
      this.canvasElement.removeEventListener('wheel', this.onMouseWheel);
      this.canvasElement.removeEventListener('touchstart', this.onTouchStart);
      this.canvasElement.removeEventListener('touchmove', this.onTouchMove);
      this.canvasElement.removeEventListener('touchend', this.onTouchEnd);
      this.canvasElement = null;
    }
    
    // 清理文档事件监听器
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    
    // 清理拖拽事件监听器
    document.removeEventListener('mousemove', this.onDragAnnotation);
    document.removeEventListener('mouseup', this.endDragAnnotation);
    document.removeEventListener('touchmove', this.onDragAnnotation);
    document.removeEventListener('touchend', this.endDragAnnotation);
    
    // 清理窗口事件监听器
    window.removeEventListener('resize', this.onWindowResize);
    if (this.handleOrientationChange) {
      window.removeEventListener('orientationchange', this.handleOrientationChange);
      this.handleOrientationChange = null;
    }
    
    // 清理 Three.js 对象
    if (this.sphere) {
      if (this.sphere.material) {
        if (this.sphere.material.map) {
          this.sphere.material.map.dispose();
          this.sphere.material.map = null;
        }
        this.sphere.material.dispose();
        this.sphere.material = null;
      }
      if (this.sphere.geometry) {
        this.sphere.geometry.dispose();
        this.sphere.geometry = null;
      }
      if (this.scene) {
        this.scene.remove(this.sphere);
      }
      this.sphere = null;
    }
    
    // 清理场景中的所有对象
    if (this.scene) {
      while (this.scene.children.length > 0) {
        const object = this.scene.children[0];
        this.scene.remove(object);
        
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => {
              if (material.map) material.map.dispose();
              material.dispose();
            });
          } else {
            if (object.material.map) object.material.map.dispose();
            object.material.dispose();
          }
        }
      }
      this.scene = null;
    }
    
    // 清理渲染器
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer.domElement = null;
      this.renderer = null;
    }
    
    // 清理相机
    this.camera = null;
  },
  methods: {
    initThreeJS() {
      const container = this.$refs.viewerContainer;
      const canvas = this.$refs.canvas;

      if (!container || !canvas) {
        this.showError('初始化失败：容器未找到');
        return;
      }

      try {
        // 获取容器尺寸，如果为0则使用默认值
        const width = container.clientWidth || 800;
        const height = container.clientHeight || 600;

        // 创建场景
        this.scene = new THREE.Scene();

        // 创建相机
        this.camera = new THREE.PerspectiveCamera(
          this.initialFov,
          width / height,
          0.1,
          1000
        );
        this.camera.position.set(0, 0, 0);

        // 创建射线投射器（用于3D点击检测）
        this.raycaster = new THREE.Raycaster();

        // 创建渲染器
        this.renderer = new THREE.WebGLRenderer({ 
          canvas, 
          antialias: false, // 移动端关闭抗锯齿以节省内存
          powerPreference: 'high-performance',
          preserveDrawingBuffer: true // 允许截图
        });
        this.renderer.setSize(width, height);
        // 限制 pixelRatio，移动端最多2倍，避免内存溢出
        const pixelRatio = Math.min(window.devicePixelRatio, 2);
        this.renderer.setPixelRatio(pixelRatio);

        // 加载全景图纹理
        this.loadPanorama();

        // 保存canvas引用以便清理
        this.canvasElement = canvas;

        // 添加事件监听
        canvas.addEventListener('mousedown', this.onMouseDown);
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);
        canvas.addEventListener('wheel', this.onMouseWheel);
        canvas.addEventListener('touchstart', this.onTouchStart);
        canvas.addEventListener('touchmove', this.onTouchMove);
        canvas.addEventListener('touchend', this.onTouchEnd);
      } catch (error) {
        console.error('Three.js 初始化失败:', error);
        this.showError('初始化失败：' + error.message);
      }
    },

    async loadPanorama() {
      if (!this.src) {
        this.showError('未提供图片地址');
        return;
      }

      // 取消之前的加载请求
      if (this.abortController) {
        this.abortController.abort();
      }

      // 【关键】立即清理旧资源，避免内存峰值
      if (this.sphere) {
        if (this.sphere.material) {
          if (this.sphere.material.map) {
            this.sphere.material.map.dispose();
            this.sphere.material.map = null;
          }
          this.sphere.material.dispose();
          this.sphere.material = null;
        }
        if (this.sphere.geometry) {
          this.sphere.geometry.dispose();
          this.sphere.geometry = null;
        }
        if (this.scene) {
          this.scene.remove(this.sphere);
        }
        this.sphere = null;
        
        // 给浏览器一点时间进行垃圾回收
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // 创建新的 AbortController
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.loading = true;
      this.loadError = false;
      this.loadProgress = 0;

      let simulatedProgressInterval = null;

      // 根据模式选择URL：如果有fast_url且使用快速模式，则使用fast_url
      const fetchUrl = (this.fastUrl && this.useFastMode) ? this.fastUrl : this.src;

      try {
        // 使用 fetch 加载图片
        const response = await fetch(fetchUrl, { signal });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // 获取文件总大小
        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;

        // 如果没有 content-length，使用模拟进度
        let simulatedProgress = 0;
        
        if (total === 0) {
          // 启动模拟进度（缓慢增长到90%）
          simulatedProgressInterval = setInterval(() => {
            if (simulatedProgress < 90) {
              simulatedProgress += Math.random() * 10;
              simulatedProgress = Math.min(simulatedProgress, 90);
              this.loadProgress = Math.round(simulatedProgress);
              this.$emit('progress', this.loadProgress);
            }
          }, 200);
        }

        // 读取数据流
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          chunks.push(value);
          receivedLength += value.length;

            // 更新进度
            if (total > 0) {
              const progress = Math.round((receivedLength / total) * 100);
              this.loadProgress = progress;
              this.$emit('progress', progress);
            }
          }
          
          // 清除模拟进度定时器
          if (simulatedProgressInterval) {
            clearInterval(simulatedProgressInterval);
          }

        // 合并所有数据块
        const chunksAll = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          chunksAll.set(chunk, position);
          position += chunk.length;
        }

        // 创建 Blob 和 URL
        const blob = new Blob([chunksAll]);
        const imageUrl = URL.createObjectURL(blob);

        // 使用 THREE.TextureLoader 加载 Blob URL
        const textureLoader = new THREE.TextureLoader();
        const texture = await new Promise((resolve, reject) => {
          textureLoader.load(
            imageUrl,
            (tex) => resolve(tex),
            undefined,
            (err) => reject(err)
          );
        });

        // 释放 Blob URL
        URL.revokeObjectURL(imageUrl);

        // 检查是否被取消
        if (signal.aborted) {
          if (texture) {
            texture.dispose();
          }
          return;
        }
        
        // 优化纹理设置（移动端友好）
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        // 限制各向异性过滤，减少内存占用
        texture.anisotropy = Math.min(this.renderer.capabilities.getMaxAnisotropy(), 4);

        // 创建球体几何体
        const geometry = new THREE.SphereGeometry(
          this.sphereRadius,
          this.sphereSegments.width,
          this.sphereSegments.height
        );
        // 翻转几何体，使纹理在内部可见
        geometry.scale(-1, 1, 1);

        // 创建材质
        const material = new THREE.MeshBasicMaterial({
          map: texture,
        });

        // 创建球体网格（旧资源已在函数开始时清理）
        this.sphere = new THREE.Mesh(geometry, material);
        this.scene.add(this.sphere);

        this.loading = false;
        this.loadProgress = 100;
        
        // 确保渲染器尺寸正确
        this.$nextTick(() => {
          this.onWindowResize();
          
          // 开始动画循环
          if (!this.animationId) {
            this.animate();
          }
        });

        // 触发加载完成事件
        this.$emit('load', { success: true });
        
        // 清理 AbortController
        this.abortController = null;

      } catch (error) {
        // 清除模拟进度定时器
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
        }

        // 检查是否是用户取消的请求
        if (error.name === 'AbortError') {
          return;
        }

        this.showError('加载全景图失败：' + error.message);
        this.loading = false;
        this.abortController = null;
      }
    },

    showError(message) {
      this.loading = false;
      this.loadError = true;
      this.errorMessage = message;
      this.$emit('error', { message });
    },

    retryLoad() {
      this.loadPanorama();
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate);

      // 惯性滚动
      if (this.inertiaActive && !this.isUserInteracting) {
        // 应用速度
        this.lon -= this.velocityX * 0.1;
        this.lat += this.velocityY * 0.1;
        
        // 摩擦力减速（0.92 是摩擦系数，值越小减速越快）
        this.velocityX *= 0.92;
        this.velocityY *= 0.92;
        
        // 当速度很小时停止惯性
        const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
        if (speed < 0.1) {
          this.inertiaActive = false;
          this.velocityX = 0;
          this.velocityY = 0;
        }
      }

      // 检查用户是否无操作3秒
      const now = Date.now();
      const timeSinceLastInteraction = now - this.lastUserInteraction;
      if (timeSinceLastInteraction >= 3000) {
        this.shouldResetLat = true;
      }

      // 自动旋转（独立判断，不受时间检查影响）
      if (this.autoRotateEnabled && !this.isUserInteracting && !this.inertiaActive) {
        this.lon += this.autoRotateSpeed;
        
        // 如果 lat（z轴视角）不为0，且用户无操作3秒后，缓慢归零到平视，同时旋转
        if (this.shouldResetLat && Math.abs(this.lat) > 0.1) {
          // 更缓慢地归零
          this.lat *= 0.999;
          if (Math.abs(this.lat) < 0.1) {
            this.lat = 0;
          }
        }
      }

      this.lat = Math.max(-85, Math.min(85, this.lat));
      this.phi = THREE.MathUtils.degToRad(90 - this.lat);
      this.theta = THREE.MathUtils.degToRad(this.lon);

      const x = this.sphereRadius * Math.sin(this.phi) * Math.cos(this.theta);
      const y = this.sphereRadius * Math.cos(this.phi);
      const z = this.sphereRadius * Math.sin(this.phi) * Math.sin(this.theta);

      this.camera.lookAt(x, y, z);

      this.renderer.render(this.scene, this.camera);

      // 发射视角变化事件
      const currentRotation = {
        x: Math.round(this.lon * 100) / 100,
        y: Math.round(this.lat * 100) / 100,
        z: 0,
        zoom: Math.round(this.camera.fov * 100) / 100
      };
      
      // 只在值有变化时发射事件，避免过度触发
      if (
        currentRotation.x !== this.lastEmittedRotation.x ||
        currentRotation.y !== this.lastEmittedRotation.y ||
        currentRotation.zoom !== this.lastEmittedRotation.zoom
      ) {
        this.lastEmittedRotation = currentRotation;
        this.$emit('rotation-change', currentRotation);
      }

      // 批注位置通过style绑定自动更新，不需要频繁更新key
      // 移除频繁更新key的逻辑，避免抖动
    },

    onMouseDown(event) {
      // 如果正在拖拽批注，不处理全景图的交互
      if (this.draggingAnnotation) {
        return;
      }
      
      // 检查点击目标是否是批注元素
      const target = event.target;
      if (target && (
        target.closest('.annotation-marker') || 
        target.closest('.annotation-content-inline') ||
        target.closest('.marker-dot')
      )) {
        // 如果点击的是批注，不阻止默认行为，让批注的点击事件正常触发
        return;
      }

      event.preventDefault();

      this.isUserInteracting = true;
      this.inertiaActive = false; // 停止惯性
      this.velocityX = 0;
      this.velocityY = 0;
      this.shouldResetLat = false; // 用户操作时停止视角归零
      this.lastUserInteraction = Date.now(); // 更新最后操作时间
      
      this.onMouseDownMouseX = event.clientX;
      this.onMouseDownMouseY = event.clientY;
      this.onMouseDownLon = this.lon;
      this.onMouseDownLat = this.lat;
      
      // 初始化速度追踪
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.lastMoveTime = Date.now();
    },

    onMouseMove(event) {
      // 如果正在拖拽批注，不处理全景图的移动
      if (this.draggingAnnotation) {
        return;
      }
      
      if (this.isUserInteracting && this.onMouseDownMouseX !== null && this.onMouseDownMouseY !== null) {
        this.lon = (this.onMouseDownMouseX - event.clientX) * 0.1 + this.onMouseDownLon;
        this.lat = (event.clientY - this.onMouseDownMouseY) * 0.1 + this.onMouseDownLat;
        
        // 计算速度（用于惯性）
        const now = Date.now();
        const deltaTime = now - this.lastMoveTime;
        
        if (deltaTime > 0) {
          const deltaX = event.clientX - this.lastMouseX;
          const deltaY = event.clientY - this.lastMouseY;
          
          // 速度 = 移动距离 / 时间，再乘以系数
          this.velocityX = (deltaX / deltaTime) * 10;
          this.velocityY = (deltaY / deltaTime) * 10;
          
          this.lastMouseX = event.clientX;
          this.lastMouseY = event.clientY;
          this.lastMoveTime = now;
        }
      }
    },

    onMouseUp(event) {
      // 如果正在拖拽批注，不处理全景图的交互
      if (this.draggingAnnotation) {
        return;
      }
      
      const wasInteracting = this.isUserInteracting;
      this.isUserInteracting = false;

      if (this.onMouseDownMouseX === null || this.onMouseDownMouseY === null) {
        return;
      }

      // 计算拖动距离
      const dragDistance = Math.sqrt(
        Math.pow(event.clientX - this.onMouseDownMouseX, 2) + 
        Math.pow(event.clientY - this.onMouseDownMouseY, 2)
      );
      
      // 如果有拖动操作（距离大于5px）
      if (dragDistance >= 5) {
        // 启动惯性滚动（如果速度足够大）
        const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
        if (speed > 0.5) {
          this.inertiaActive = true;
        }
      }
      
      // 如果是批注模式且点击时没有拖动，触发事件让父组件处理
      if (
        this.enableAnnotation &&
        this.annotationMode &&
        wasInteracting &&
        dragDistance < 5
      ) {
        this.$emit('add-annotation-click', { clientX: event.clientX, clientY: event.clientY });
      }
    },

    onMouseWheel(event) {
      event.preventDefault();
      this.shouldResetLat = false; // 用户操作时停止视角归零
      this.lastUserInteraction = Date.now(); // 更新最后操作时间
      const fov = this.camera.fov + event.deltaY * 0.05;
      this.camera.fov = THREE.MathUtils.clamp(fov, 30, 90);
      this.camera.updateProjectionMatrix();
    },

    onTouchStart(event) {
      // 如果正在拖拽批注，不处理全景图的交互
      if (this.draggingAnnotation) {
        return;
      }
      
      // 检查触摸目标是否是批注元素
      const target = event.target;
      if (target && (
        target.closest('.annotation-marker') || 
        target.closest('.annotation-content-inline') ||
        target.closest('.marker-dot')
      )) {
        // 如果触摸的是批注，不阻止默认行为，让批注的点击事件正常触发
        return;
      }

      if (event.touches.length === 1) {
        event.preventDefault();
        
        this.isUserInteracting = true;
        this.inertiaActive = false; // 停止惯性
        this.velocityX = 0;
        this.velocityY = 0;
        this.shouldResetLat = false; // 用户操作时停止视角归零
        this.lastUserInteraction = Date.now(); // 更新最后操作时间
        
        this.onMouseDownMouseX = event.touches[0].pageX;
        this.onMouseDownMouseY = event.touches[0].pageY;
        this.onMouseDownLon = this.lon;
        this.onMouseDownLat = this.lat;
        
        // 初始化速度追踪
        this.lastMouseX = event.touches[0].pageX;
        this.lastMouseY = event.touches[0].pageY;
        this.lastMoveTime = Date.now();
      } else if (event.touches.length === 2) {
        // 双指缩放
        event.preventDefault();
        this.isUserInteracting = true;
        this.inertiaActive = false;
        
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const distance = Math.sqrt(
          Math.pow(touch2.pageX - touch1.pageX, 2) + 
          Math.pow(touch2.pageY - touch1.pageY, 2)
        );
        
        this.initialPinchDistance = distance;
        this.pinchInitialFov = this.camera ? this.camera.fov : 75;
        this.shouldResetLat = false;
        this.lastUserInteraction = Date.now();
      }
    },

    onTouchMove(event) {
      // 如果正在拖拽批注，不处理全景图的移动
      if (this.draggingAnnotation) {
        return;
      }
      
      if (event.touches.length === 1) {
        event.preventDefault();
        
        const touchX = event.touches[0].pageX;
        const touchY = event.touches[0].pageY;
        
        this.lon = (this.onMouseDownMouseX - touchX) * 0.1 + this.onMouseDownLon;
        this.lat = (touchY - this.onMouseDownMouseY) * 0.1 + this.onMouseDownLat;
        
        // 计算速度（用于惯性）
        const now = Date.now();
        const deltaTime = now - this.lastMoveTime;
        
        if (deltaTime > 0) {
          const deltaX = touchX - this.lastMouseX;
          const deltaY = touchY - this.lastMouseY;
          
          // 速度 = 移动距离 / 时间，再乘以系数
          this.velocityX = (deltaX / deltaTime) * 10;
          this.velocityY = (deltaY / deltaTime) * 10;
          
          this.lastMouseX = touchX;
          this.lastMouseY = touchY;
          this.lastMoveTime = now;
        }
      } else if (event.touches.length === 2 && this.camera) {
        // 双指缩放
        event.preventDefault();
        
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const distance = Math.sqrt(
          Math.pow(touch2.pageX - touch1.pageX, 2) + 
          Math.pow(touch2.pageY - touch1.pageY, 2)
        );
        
        if (this.initialPinchDistance > 0) {
          // 计算缩放比例
          const scale = distance / this.initialPinchDistance;
          // 根据缩放比例调整FOV（视场角）
          // scale > 1 表示放大（手指远离），FOV减小（视野变窄）
          // scale < 1 表示缩小（手指靠近），FOV增大（视野变广）
          const newFov = this.pinchInitialFov / scale;
          this.camera.fov = THREE.MathUtils.clamp(newFov, 30, 90);
          this.camera.updateProjectionMatrix();
        }
      }
    },

    onTouchEnd(event) {
      // 如果正在拖拽批注，不处理全景图的交互
      if (this.draggingAnnotation) {
        return;
      }
      
      // 如果只剩一个手指或没有手指，结束交互
      if (event.touches.length <= 1) {
        this.isUserInteracting = false;
        
        // 重置双指缩放状态
        if (event.touches.length === 0) {
          this.initialPinchDistance = 0;
        }
      }

      if (event.changedTouches.length === 1 && this.onMouseDownMouseX !== null && this.onMouseDownMouseY !== null && event.touches.length === 0) {
        const dragDistance = Math.sqrt(
          Math.pow(event.changedTouches[0].pageX - this.onMouseDownMouseX, 2) + 
          Math.pow(event.changedTouches[0].pageY - this.onMouseDownMouseY, 2)
        );

        if (dragDistance >= 5) {
          // 启动惯性滚动（如果速度足够大）
          const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
          if (speed > 0.5) {
            this.inertiaActive = true;
          }
        }

        if (
          this.enableAnnotation &&
          this.annotationMode &&
          dragDistance < 5
        ) {
          this.$emit('add-annotation-click', { 
            clientX: event.changedTouches[0].pageX, 
            clientY: event.changedTouches[0].pageY 
          });
        }
      }
    },

    onWindowResize() {
      const container = this.$refs.viewerContainer;
      if (container && this.camera && this.renderer) {
        // 获取容器尺寸，如果为0则使用窗口尺寸
        const width = container.clientWidth || window.innerWidth;
        const height = container.clientHeight || window.innerHeight;
        
        // 更新相机宽高比和投影矩阵
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        
        // 更新渲染器尺寸
        this.renderer.setSize(width, height);
        
        // 限制 pixelRatio，移动端最多2倍，避免内存溢出
        const pixelRatio = Math.min(window.devicePixelRatio, 2);
        this.renderer.setPixelRatio(pixelRatio);
      }
    },


    // 公开方法：添加批注到3D点
    async addAnnotationAtPosition(clientX, clientY, content) {
      if (!this.sphere || !this.raycaster) {
        return null;
      }

      const container = this.$refs.viewerContainer;
      const rect = container.getBoundingClientRect();
      
      const mouse = new THREE.Vector2();
      mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(mouse, this.camera);
      const intersects = this.raycaster.intersectObject(this.sphere);

      if (intersects.length > 0) {
        const point = intersects[0].point;
        
        try {
          const data = {
            project_id: this.projectId,
            view_id: this.viewId,
            scene_id: this.sceneId,
            content: content,
            position: {
              x: point.x,
              y: point.y,
              z: point.z,
            },
          };

          const response = await this.$api.post('uav/comments', data);
          const commentData = response.data.data;
          
          const annotation = {
            id: commentData.id,
            content: commentData.content,
            position: data.position,
            timestamp: commentData.created_at || new Date().toISOString(),
            user_id: commentData.created_by,
            created_by: commentData.created_by, // 保存created_by用于权限检查
            user_name: commentData.user_name,
            created_by_name: commentData.created_by_name,
            created_by_avatar: commentData.created_by_avatar,
            replies: [],
          };

          this.annotations.push(annotation);
          this.$emit('annotation-add', annotation);
          
          return annotation;
        } catch (error) {
          console.error('保存批注失败:', error);
          throw error;
        }
      }
      
      return null;
    },
    
    // 公开方法：设置自动旋转
    setAutoRotate(enabled) {
      this.autoRotateEnabled = enabled;
      if (enabled) {
        // 启用自动旋转时，重置最后操作时间，3秒后开始视角归零
        this.lastUserInteraction = Date.now();
        this.shouldResetLat = false;
      } else {
        // 禁用自动旋转时，停止视角归零
        this.shouldResetLat = false;
      }
    },

    // 切换加载模式
    toggleMode() {
      this.useFastMode = !this.useFastMode;
      // 强制清理缓存，防止内存溢出
      if (this.sphere && this.sphere.material && this.sphere.material.map) {
        this.sphere.material.map.dispose();
        this.sphere.material.map = null;
      }
      // 触发垃圾回收
      if (window.gc) {
        window.gc();
      }
      // 延迟重新加载，给浏览器时间清理内存
      setTimeout(() => {
        this.loadPanorama();
      }, 100);
    },
    

    getAnnotationPosition(annotation) {
      if (!this.camera || !annotation || !annotation.position) {
        return {
          left: '-1000px',
          top: '-1000px',
          visibility: 'hidden',
          pointerEvents: 'none',
        };
      }

      const vector = new THREE.Vector3(
        annotation.position.x,
        annotation.position.y,
        annotation.position.z
      );

      const cameraDirection = new THREE.Vector3();
      this.camera.getWorldDirection(cameraDirection);
      const vectorToAnnotation = vector.clone().normalize();
      const dotProduct = cameraDirection.dot(vectorToAnnotation);

      if (dotProduct < 0.1) {
        return {
          left: '-1000px',
          top: '-1000px',
          visibility: 'hidden',
          pointerEvents: 'none',
        };
      }

      vector.project(this.camera);

      const container = this.$refs.viewerContainer;
      if (!container) {
        return {
          left: '-1000px',
          top: '-1000px',
          visibility: 'hidden',
          pointerEvents: 'none',
        };
      }

      const x = (vector.x * 0.5 + 0.5) * container.clientWidth;
      const y = (-(vector.y * 0.5) + 0.5) * container.clientHeight;

      // 使用Math.round避免小数点导致的抖动
      return {
        left: `${Math.round(x)}px`,
        top: `${Math.round(y)}px`,
        visibility: 'visible',
        pointerEvents: 'auto',
      };
    },

    showAnnotationDetail(annotation) {
      // 如果点击的是已选中的批注，则关闭它；否则打开它
      if (this.selectedAnnotation && this.selectedAnnotation.id === annotation.id) {
        this.closeAnnotationDetail();
      } else {
        this.selectedAnnotation = annotation;
        this.showAllReplies = false;
        this.replyingTo = null;
        this.replyText = '';
        // 通知父组件暂停自动旋转
        this.$emit('annotation-interaction');
      }
    },

    closeAnnotationDetail() {
      this.selectedAnnotation = null;
      this.showAllReplies = false;
      this.replyingTo = null;
      this.replyText = '';
    },
    
    handleAnnotationHover() {
      // 鼠标悬停在批注上时，通知父组件暂停自动旋转
      this.$emit('annotation-interaction');
    },

    startReply(annotationId) {
      this.replyingTo = annotationId;
      this.replyText = '';
      this.$nextTick(() => {
        if (this.$refs.replyInput) {
          this.$refs.replyInput.focus();
        }
      });
    },

    cancelReply() {
      this.replyingTo = null;
      this.replyText = '';
    },

    async saveReply() {
      if (!this.replyText.trim()) {
        alert('请输入回复内容');
        return;
      }

      try {
        const data = {
          project_id: this.projectId,
          view_id: this.viewId,
          scene_id: this.sceneId,
          content: this.replyText,
          reply_id: this.replyingTo,
        };

        const response = await this.$api.post('uav/comments', data);
        const replyData = response.data.data;
        
        const reply = {
          id: replyData.id,
          content: replyData.content,
          timestamp: replyData.created_at || new Date().toISOString(),
          user_id: replyData.created_by || replyData.user_id,
          user_name: replyData.created_by_name || replyData.user_name || '匿名用户',
          created_by_name: replyData.created_by_name || replyData.user_name || '匿名用户',
          created_by_avatar: replyData.created_by_avatar,
          reply_id: replyData.reply_id,
        };

        // 同步更新 annotations 数组
        const annotation = this.annotations.find(a => a.id === this.replyingTo);
        if (annotation) {
          if (!annotation.replies) {
            annotation.replies = [];
          }
          // 检查是否已存在，避免重复添加
          const exists = annotation.replies.find(r => r.id === reply.id);
          if (!exists) {
            annotation.replies.push(reply);
          }
        }
        
        // selectedAnnotation是annotations数组中的引用，所以会自动更新
        // 但为了确保UI更新，我们强制触发响应式更新
        this.$forceUpdate();

        this.replyText = '';
        this.replyingTo = null;

        this.$emit('reply-add', reply);
      } catch (error) {
        console.error('保存回复失败:', error);
        alert('保存回复失败，请重试');
      }
    },

    async deleteReply(replyId) {
      if (!confirm('确定要删除这条回复吗？')) {
        return;
      }

      try {
        await this.$api.delete(`uav/comments/${replyId}`);
        
        // 从选中的批注中删除回复
        if (this.selectedAnnotation && this.selectedAnnotation.replies) {
          const index = this.selectedAnnotation.replies.findIndex(r => r.id === replyId);
          if (index !== -1) {
            this.selectedAnnotation.replies.splice(index, 1);
          }
        }

        // 同步更新 annotations 数组
        this.annotations.forEach(annotation => {
          if (annotation.replies) {
            const index = annotation.replies.findIndex(r => r.id === replyId);
            if (index !== -1) {
              annotation.replies.splice(index, 1);
            }
          }
        });

        this.$emit('reply-delete', { id: replyId });
      } catch (error) {
        console.error('删除回复失败:', error);
        alert('删除回复失败，请重试');
      }
    },

    canDeleteComment(comment) {
      // 只有评论作者才能删除（检查created_by或user_id）
      if (!this.isUserLoggedIn) return false;
      const commentUserId = comment.created_by || comment.user_id;
      return commentUserId === this.currentUserId;
    },
    
    canDragAnnotation(annotation) {
      // 只有批注创建者才能拖拽
      if (!this.isUserLoggedIn) return false;
      if (!this.annotationMode) return false;
      const annotationUserId = annotation.created_by || annotation.user_id;
      return annotationUserId === this.currentUserId;
    },
    
    startDragAnnotation(event, annotation) {
      // 检查权限
      if (!this.canDragAnnotation(annotation)) {
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();
      
      // 立即设置拖拽状态，阻止全景图交互
      this.draggingAnnotation = annotation;
      this.isDraggingAnnotation = false;
      
      // 阻止全景图的交互
      this.isUserInteracting = true;
      
      // 重置全景图的交互状态，防止冲突
      this.onMouseDownMouseX = null;
      this.onMouseDownMouseY = null;
      
      // 添加拖拽事件监听
      if (event.type === 'mousedown' || event.type === 'touchstart') {
        if (event.type === 'mousedown') {
          document.addEventListener('mousemove', this.onDragAnnotation);
          document.addEventListener('mouseup', this.endDragAnnotation);
        } else {
          document.addEventListener('touchmove', this.onDragAnnotation);
          document.addEventListener('touchend', this.endDragAnnotation);
        }
      }
    },
    
    onDragAnnotation(event) {
      if (!this.draggingAnnotation || !this.sphere) return;
      
      this.isDraggingAnnotation = true;
      
      const container = this.$refs.viewerContainer;
      const rect = container.getBoundingClientRect();
      
      // 获取鼠标或触摸位置
      let clientX, clientY;
      if (event.type === 'mousemove') {
        clientX = event.clientX;
        clientY = event.clientY;
      } else if (event.type === 'touchmove') {
        if (event.touches.length === 0) return;
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        return;
      }
      
      const mouse = new THREE.Vector2();
      mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(mouse, this.camera);
      const intersects = this.raycaster.intersectObject(this.sphere);

      if (intersects.length > 0) {
        const point = intersects[0].point;
        this.draggingAnnotation.position.x = point.x;
        this.draggingAnnotation.position.y = point.y;
        this.draggingAnnotation.position.z = point.z;
      }
      
      event.preventDefault();
    },
    
    async endDragAnnotation(event) {
      // 移除事件监听器
      document.removeEventListener('mousemove', this.onDragAnnotation);
      document.removeEventListener('mouseup', this.endDragAnnotation);
      document.removeEventListener('touchmove', this.onDragAnnotation);
      document.removeEventListener('touchend', this.endDragAnnotation);
      
      // 恢复全景图交互
      this.isUserInteracting = false;
      
      if (this.draggingAnnotation && this.isDraggingAnnotation) {
        // 保存拖拽后的批注位置
        try {
          await this.$api.patch(`uav/comments/${this.draggingAnnotation.id}`, {
            position: JSON.stringify(this.draggingAnnotation.position)
          });
          this.$emit('annotation-update', this.draggingAnnotation);
        } catch (error) {
          console.error('更新批注位置失败:', error);
          alert('更新批注位置失败');
        }
      }
      
      setTimeout(() => {
        this.draggingAnnotation = null;
        this.isDraggingAnnotation = false;
      }, 100);
    },

    async deleteAnnotation(id) {
      try {
        await this.$api.delete(`uav/comments/${id}`);
        
        const index = this.annotations.findIndex((a) => a.id === id);
        if (index !== -1) {
          const annotation = this.annotations[index];
          this.annotations.splice(index, 1);
          this.selectedAnnotation = null;
          this.$emit('annotation-delete', annotation);
        }
      } catch (error) {
        console.error('删除批注失败:', error);
        alert('删除批注失败，请重试');
      }
    },

    async loadAnnotations() {
      if (!this.viewId) {
        console.warn('未提供 viewId，无法加载批注');
        return;
      }

      try {
        const response = await this.$api.get(`public/uav-views/${this.viewId}/comments`);
        const comments = response.data.data || [];
        
        // 按 reply_id 归组：主评论和回复分开
        const mainComments = [];
        const replyMap = {};

        comments.forEach(comment => {
          if (!comment.reply_id) {
            // 主评论
            mainComments.push({
              id: comment.id,
              content: comment.content,
              position: comment.position,
              timestamp: comment.created_at || comment.timestamp,
              user_id: comment.created_by,
              created_by: comment.created_by, // 保存created_by用于权限检查
              user_name: comment.user_name,
              created_by_avatar: comment.created_by_avatar,
              created_by_name: comment.created_by_name,
              replies: [],
            });
          } else {
            // 回复
            if (!replyMap[comment.reply_id]) {
              replyMap[comment.reply_id] = [];
            }
            replyMap[comment.reply_id].push({
              id: comment.id,
              content: comment.content,
              timestamp: comment.created_at || comment.timestamp,
              user_id: comment.created_by,
              created_by: comment.created_by, // 保存created_by用于权限检查
              user_name: comment.user_name,
              created_by_avatar: comment.created_by_avatar,
              created_by_name: comment.created_by_name,
              reply_id: comment.reply_id,
            });
          }
        });

        // 将回复关联到主评论
        mainComments.forEach(comment => {
          if (replyMap[comment.id]) {
            comment.replies = replyMap[comment.id];
            // 按时间排序回复
            comment.replies.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          }
        });

        // 按时间倒序排列主评论（最新的在前）
        mainComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        this.annotations = mainComments;
      } catch (error) {
        this.annotations = [];
      }
    },

    takePhoto(){
        // 将渲染页面的图像保存为照片并下载
        try {
          const canvas = this.$refs.canvas;
          if (!canvas) {
            console.error('Canvas element not found');
            return;
          }
          
          // 确保渲染完成后再截图
          this.renderer.render(this.scene, this.camera);
          
          // 使用 preserveDrawingBuffer 或者直接从 renderer 获取
          const image = canvas.toDataURL('image/jpeg', 0.95);
          
          if (!image || image === 'data:,') {
            console.error('Canvas is empty');
            this.$message?.error('截图失败：画布为空');
            return;
          }
          
          const a = document.createElement('a');
          a.href = image;
          a.download = `panorama-${Date.now()}.jpg`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          this.$message?.success('照片已保存到本地');
        } catch (error) {
          console.error('截图失败:', error);
          this.$message?.error('截图失败');
        }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    
    // 获取用户名首字母
    getInitials(name) {
      if (!name) return '?';
      const names = name.trim().split(' ');
      if (names.length >= 2) {
        return names[0][0] + names[1][0];
      }
      return name[0] || '?';
    },
    
    // 处理头像加载错误
    handleAvatarError(event) {
      event.target.style.display = 'none';
    },
  },
};
</script>

<style scoped>
.panorama-container {
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
  position: relative;
}

/* 加载中样式 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(102, 126, 234, 0.2);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 20px;
  color: #667eea;
  font-size: 16px;
  font-weight: 500;
}

.loading-progress {
  margin-top: 20px;
  width: 300px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(102, 126, 234, 0.2);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  margin-top: 10px;
  color: #667eea;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

.loading-progress-bar {
  margin-top: 15px;
  width: 300px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.loading-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* 错误样式 */
.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 20px;
}

.error-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.error-text {
  color: #ff6b6b;
  font-size: 16px;
  text-align: center;
  margin-bottom: 20px;
  max-width: 400px;
}

.retry-btn {
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: #5568d3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 全景图内容 */
.panorama-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 加载中的内容样式 */
.panorama-content.is-loading {
  opacity: 0.5;
  pointer-events: none;
}

/* 模式切换按钮 */
.mode-toggle-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 20;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.4);
  color: white;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  user-select: none;
}

.mode-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: scale(1.05);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);
}

.mode-toggle-btn:active {
  transform: scale(0.98);
}

/* 底部浮动工具栏 */
.floating-toolbar {
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 20;
  background: rgba(0, 0, 0, 0.5);
  padding: 12px;
  border-radius: 50px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.toolbar-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}

.toolbar-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
}

.toolbar-btn .icon {
  display: inline-block;
}

.panorama-viewer {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #000;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
  cursor: grab;
  position: relative;
  z-index: 1;
}

canvas:active {
  cursor: grabbing;
}

/* 批注标记 */
.annotation-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: 100;
  pointer-events: auto !important;
  opacity: 0.9;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.annotation-marker:hover {
  opacity: 1;
  z-index: 150;
}

.annotation-marker:hover .marker-dot {
  transform: scale(1.15);
}

.marker-dot {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #667eea;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  animation: pulse-marker 2s ease-in-out infinite;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  cursor: pointer;
  pointer-events: auto;
}

.marker-dot:active {
  transform: scale(1.2);
  animation: none;
}

.marker-dot-draggable {
  cursor: move;
}

.marker-dot-draggable:active {
  cursor: grabbing;
}

/* 批注内容显示 */
.annotation-content-inline {
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid #667eea;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  min-width: 120px;
  max-width: 250px;
  transition: all 0.3s ease;
  cursor: pointer;
  pointer-events: auto !important;
  user-select: none;
}

.annotation-marker:hover .annotation-content-inline {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.annotation-content-inline:active {
  transform: scale(0.98);
}

.annotation-text-inline {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
  max-height: 80px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 4px;
}

.annotation-text-inline::-webkit-scrollbar {
  width: 4px;
}

.annotation-text-inline::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.annotation-text-inline::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 2px;
}

.annotation-text-inline::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.replies-count-inline {
  font-size: 11px;
  color: #667eea;
  font-weight: 600;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(102, 126, 234, 0.2);
}

@keyframes pulse-marker {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  50% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(102, 126, 234, 0);
  }
}

/* 批注弹窗 */
.annotation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.annotation-content {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease;
  backdrop-filter: blur(10px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.annotation-header-modal {
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.annotation-header-modal h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.annotation-body {
  padding: 20px;
  max-height: 500px;
  overflow-y: auto;
}

.annotation-body::-webkit-scrollbar {
  width: 6px;
}

.annotation-body::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.annotation-body::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.annotation-body::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}

/* 评论项 */
.comment-item {
  margin-bottom: 16px;
  padding-bottom: 16px;
}

.main-comment {
  border-bottom: 2px solid #e0e0e0;
}

.reply-comment {
  background: rgba(248, 249, 250, 0.5);
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #667eea;
  backdrop-filter: blur(10px);
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.comment-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.user-avatar-small {
  width: 24px;
  height: 24px;
}

.user-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
}

.user-avatar-placeholder.user-avatar-small {
  width: 24px;
  height: 24px;
  font-size: 11px;
}

.comment-user {
  font-weight: 600;
  color: #667eea;
  font-size: 14px;
}

.comment-time {
  font-size: 12px;
  color: #999;
}

.comment-text {
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-actions {
  display: flex;
  gap: 8px;
}

.btn-reply,
.btn-delete-comment {
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-reply {
  background: #e3f2fd;
  color: #1976d2;
}

.btn-reply:hover {
  background: #bbdefb;
}

.btn-delete-comment {
  background: #ffebee;
  color: #c62828;
}

.btn-delete-comment:hover {
  background: #ffcdd2;
}

/* 回复部分 */
.replies-section {
  margin-top: 16px;
}

.replies-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.replies-count {
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.btn-show-more {
  padding: 4px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-show-more:hover {
  background: #e0e0e0;
}

/* 回复输入 */
.reply-input-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.reply-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;
  box-sizing: border-box;
  margin-bottom: 12px;
}

.reply-input:focus {
  outline: none;
  border-color: #667eea;
}

.reply-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.annotation-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.annotation-input:focus {
  outline: none;
  border-color: #667eea;
}

.annotation-footer {
  padding: 16px 20px;
  background: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel,
.btn-save {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: #e0e0e0;
  color: #666;
}

.btn-cancel:hover {
  background: #d0d0d0;
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.btn-save:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 提示横幅 */
.tip-banner {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(102, 126, 234, 0.95);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 20;
  animation: fadeInDown 0.3s ease;
  backdrop-filter: blur(10px);
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .floating-toolbar {
    bottom: 16px;
    padding: 8px;
  }

  .toolbar-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .tip-banner {
    top: 16px;
    padding: 10px 20px;
    font-size: 13px;
    max-width: 90%;
  }

  .annotation-content {
    width: 95%;
  }

  .marker-dot {
    width: 28px;
    height: 28px;
    font-size: 16px;
  }

  .annotation-content-inline {
    max-width: 180px;
    font-size: 12px;
  }

  .annotation-text-inline {
    font-size: 12px;
    max-height: 60px;
  }

  .replies-count-inline {
    font-size: 10px;
  }
}
</style>


